import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const DEFAULTS = {
  brand_name: "TherapyFlow",
  logo_url: "",
  logo_file_id: "",
  primary_color: "#4A6753",
  login_tagline: "Run your therapy center without paper.",
  footer_text: "Built for real therapy businesses.",
};

const BrandingContext = createContext({ brand: DEFAULTS, refresh: () => {} });

/**
 * Resolve the effective logo src.
 *
 * Rules (must mirror backend `branding_routes.update_branding`):
 *   1. If an uploaded logo (`logo_file_id`) exists, it always wins — an
 *      auth-scoped URL to `/api/files/{id}` is returned so `<img>` can
 *      render it directly.
 *   2. Otherwise fall back to the free-text `logo_url`.
 *   3. Empty string when neither is set — consumers should fall back to
 *      an initial-letter avatar.
 */
function computeEffectiveLogo(brand) {
  const fid = (brand?.logo_file_id || "").trim();
  if (fid) {
    // The httpOnly access_token cookie is auto-attached by the browser on
    // same-origin GETs, so a bare `/api/files/{id}` src works from any
    // `<img>` tag — no query token is required. When the app is served
    // from a different origin than the API, the backend also accepts
    // `?auth=<jwt>` for legacy callers.
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/files/${fid}`;
  }
  return (brand?.logo_url || "").trim();
}

/** Convert #RRGGBB → "h s% l%" (space-separated, ready for CSS `hsl(var(--x))`). */
function hexToHsl(hex) {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Apply brand to the DOM: document title + CSS variable overrides. */
function applyBrand(brand) {
  if (typeof document === "undefined") return;
  document.title = brand.brand_name || "TherapyFlow";
  const hsl = hexToHsl(brand.primary_color);
  if (hsl) {
    document.documentElement.style.setProperty("--primary", hsl);
    document.documentElement.style.setProperty("--ring", hsl);
  }
}

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/branding");
      const merged = { ...DEFAULTS, ...data };
      merged.effective_logo_url = computeEffectiveLogo(merged);
      setBrand(merged);
      applyBrand(merged);
    } catch {
      // Public endpoint — if it fails, fall back to defaults silently.
      applyBrand(DEFAULTS);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ brand, refresh }), [brand, refresh]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
